import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} trigger - The button element.
 * @property {HTMLAnchorElement} productLink - The product link element.
 *
 * @extends Component<Refs>
 */

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];

  /** @type {(() => void) | null} */
  #pointerenterHandler = null;

  timer = /** @type {number | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    // Set up initial event listeners based on current breakpoint
    this.#handleBreakpointChange();

    // Listen for breakpoint changes
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up listeners
    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Open the quick-add modal
   * @returns {void}
   */
  #openQuickAddModal() {
    const quickAddComponent = /** @type {QuickAddComponent | null} */ (
      this.querySelector('quick-add-component')
    );

    if (!quickAddComponent) return;

    quickAddComponent.handleClick(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
      })
    );
  }

  /**
   * Set up desktop event listeners (hover)
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    /** @type {() => void} */
    const pointerenterHandler = () => {
      if (dialog.open) return;

      if (this.timer) {
        clearTimeout(this.timer);
      }

      this.timer = setTimeout(() => {
        this.timer = null;

        // Require the pointer to remain over the component
        // while the dialog placement is calculated.
        this.showDialog({ requireHover: true });
      }, 120);
    };

    this.#pointerenterHandler = pointerenterHandler;

    trigger.addEventListener('pointerenter', pointerenterHandler);

    // Observe the complete component, including the trigger,
    // the bridge area and the product dialog.
    this.addEventListener('pointerleave', this.#handlePointerLeave);
  }

  /**
   * Remove desktop event listeners
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      this.#pointerenterHandler = null;
    }

    this.removeEventListener('pointerleave', this.#handlePointerLeave);

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Handle breakpoint changes
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    // Remove existing listeners
    this.#removeDesktopListeners();

    // Set up desktop hover listeners only
    // Mobile uses on:click in the template.
    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  /**
   * Calculate the placement of the dialog.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) {
      return;
    }

    // Spacing constants
    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    // Get container bounds
    const containerRect = hotspotsContainer.getBoundingClientRect();

    // Get button dimensions
    const triggerRect = trigger.getBoundingClientRect();

    // Temporarily show the dialog invisibly so it can be measured
    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } =
      dialog.getBoundingClientRect();

    // Reset temporary dialog styles
    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');

    // Calculate button position relative to container
    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    // Calculate available space
    const spaceRight =
      containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    // Determine horizontal placement
    let x = 'right';

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    // Determine vertical placement
    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        // If top-aligned dialog overflows the bottom
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          // If bottom-aligned dialog overflows the top
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (
          dialogEndY >
          containerRect.height - CONTAINER_GAP
        ) {
          // If bottom-aligned dialog overflows the bottom
          verticalOffset = -(
            dialogEndY -
            (containerRect.height - CONTAINER_GAP)
          );
        }
      } else if (dialogStartY < CONTAINER_GAP) {
        // If top-aligned dialog overflows the top
        verticalOffset = CONTAINER_GAP - dialogStartY;
        y = 'bottom';
      }
    } else {
      // For centered horizontal placement, position below or above
      if (
        containerRect.height - buttonBottom >=
        dialogHeight + TOTAL_GAP
      ) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        // If neither fits, choose based on button position
        y =
          buttonTop < containerRect.height / 2
            ? 'bottom'
            : 'top';
      }
    }

    // Set placement data attribute
    dialog.dataset.placement = `${x},${y}`;

    // Keep the dialog inside the image container
    if (verticalOffset !== 0) {
      dialog.style.setProperty(
        '--dialog-vertical-offset',
        `${verticalOffset}px`
      );
    } else {
      dialog.style.removeProperty(
        '--dialog-vertical-offset'
      );
    }

    // Allow placement styles to be applied before opening
    return new Promise((resolve) =>
      setTimeout(resolve, 100)
    );
  }

  /**
   * Handle pointer leave.
   * @param {PointerEvent} e - The event.
   * @returns {void}
   */
  #handlePointerLeave = (e) => {
    const { dialog } = this.refs;

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (!dialog.open) return;

    const nextTarget = e.relatedTarget;

    // Do not close while moving between elements
    // inside the hotspot component.
    if (
      nextTarget instanceof Node &&
      this.contains(nextTarget)
    ) {
      return;
    }

    this.closeDialog();
  };

  /**
   * Get the product link for the hotspot product.
   * @returns {HTMLAnchorElement | null}
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle hotspot click.
   * On mobile/touch devices it opens quick-add.
   * On desktop it opens the dialog.
   *
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    if (isMobileBreakpoint() || isTouchDevice()) {
      e.preventDefault();
      e.stopPropagation();
      this.#openQuickAddModal();
    } else {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }

      this.showDialog();
    }
  };

  /**
   * Show the product dialog.
   *
   * @param {{ requireHover?: boolean }} [options]
   * @returns {Promise<void>}
   */
  showDialog = async (options = {}) => {
    const { dialog } = this.refs;
    const requireHover = options.requireHover === true;

    if (dialog.open) return;

    await this.#calculateDialogPlacement();

    // When opened by hover, cancel opening if the pointer
    // has already left during the placement calculation.
    if (requireHover && !this.matches(':hover')) {
      return;
    }

    // Protect against two simultaneous attempts to open
    if (dialog.open) return;

    dialog.dataset.showing = 'true';
    dialog.show();

    document.body.addEventListener(
      'click',
      this.lightDismissMouse
    );
    document.body.addEventListener(
      'keydown',
      this.lightDismissKeyboard
    );
    document.body.addEventListener(
      'keyup',
      this.lightDismissKeyboard
    );
  };

  /**
   * Close the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    dialog.dataset.closing = 'true';
    dialog.close();

    document.body.removeEventListener(
      'click',
      this.lightDismissMouse
    );
    document.body.removeEventListener(
      'keydown',
      this.lightDismissKeyboard
    );
    document.body.removeEventListener(
      'keyup',
      this.lightDismissKeyboard
    );

    // Keep data attributes during the closing animation
    const animations = dialog.getAnimations({
      subtree: true,
    });

    await Promise.allSettled(
      animations.map((animation) => animation.finished)
    );

    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {MouseEvent} event - The event.
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;

    if (
      (event.type === 'keydown' &&
        event.key === 'Escape') ||
      (event.type === 'keyup' &&
        !dialog.matches(
          ':is(:focus, :focus-visible, :focus-within)'
        ))
    ) {
      this.closeDialog();
    }
  };
}

// Register custom element
customElements.define(
  'product-hotspot-component',
  ProductHotspotComponent
);